import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { mutate } from "swr";
import { IconArrowLeft } from "@tabler/icons-react";
import { useAdminProductRequestDetails } from "../../hooks/useAdminProductRequestDetails";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ProductRequestDetailsInfo } from "../components/product-request-details-info";
import { ProductRequestApprovalForm } from "../components/product-request-approval-form";
import { ProductRequestStatusMessages } from "../components/product-request-status-messages";
import { ProductRequestImagesCard } from "../components/product-request-images-card";
import { AdminDatasheetCard } from "../components/admin-datasheet-card";
import { adminProductRequestsService } from "../../../api_client";
import { ProductRequestDtoStatusEnum, ReviewProductRequestDtoStatusEnum } from "../../../../api";
import type { VariantPricingTiersDto, ReviewProductRequestDto } from "../../../../api";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { statusChip } from "../../utils";

function formatSubmittedDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function AdminProductRequestReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName } = (location.state as { userId?: string; userName?: string } | null) ?? {};

  const { data: request, isLoading, error } = useAdminProductRequestDetails(id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const backRoute = userId
    ? AppRoutes.ADMIN_PRODUCT_REQUESTS_USER.replace(":userId", userId)
    : AppRoutes.ADMIN_PRODUCT_REQUESTS;

  const handleApprove = async (variantPricingTiers: VariantPricingTiersDto[]) => {
    if (!request) return;
    setIsSubmitting(true);
    try {
      const reviewDto: ReviewProductRequestDto = {
        status: ReviewProductRequestDtoStatusEnum.Approved,
        variantPricingTiers,
      };
      await adminProductRequestsService.adminReviewProductRequest(request.id, reviewDto);
      showSuccessToast("Product request approved successfully");
      mutate((key) =>
        typeof key === "string" &&
        (key.startsWith("admin-product-requests") ||
          key.startsWith("product-requests-grouped-by-user") ||
          key.startsWith("admin-product-request-details") ||
          key.startsWith("product-request-details")),
      );
      navigate(backRoute);
    } catch (err: unknown) {
      showErrorToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed to approve product request",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 pt-6 pb-6 md:px-8 md:pt-8">
        <ShimmerLoader message="Loading product request details…" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 pt-6 pb-6 md:px-8 md:pt-8">
        <Text variant="body" tone="muted">Failed to load product request. Please try again later.</Text>
      </div>
    );
  }


  return (
    <div className="flex min-h-full w-full flex-col bg-bgLight">
      <div className="flex flex-1 flex-col gap-6 px-4 pt-6 pb-6 md:px-8 md:pt-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="ghostAccent"
            size="sm"
            leftIcon={<IconArrowLeft size={18} />}
            onClick={() => navigate(AppRoutes.ADMIN_PRODUCT_REQUESTS)}
            className="!h-auto !px-0 !py-0"
          >
            <Text variant="bodySmBold" tone="inherit">Product review</Text>
          </Button>
          {userName && userId && (
            <>
              <Text variant="bodySm" tone="muted">/</Text>
              <Button
                variant="ghostAccent"
                size="sm"
                onClick={() => navigate(backRoute)}
                className="!h-auto !px-0 !py-0"
              >
                <Text variant="bodySmBold" tone="inherit">{userName}</Text>
              </Button>
            </>
          )}
          <Text variant="bodySm" tone="muted">/</Text>
          <Text variant="bodySmBold" tone="dark">{request.name}</Text>
        </div>

        {/* Title row */}
        <div className="flex items-center justify-between gap-4 flex-wrap max-md:gap-2">
          <div className="flex items-center gap-3 max-md:flex-wrap">
            <Heading variant="h2" tone="dark">{request.name}</Heading>
            {statusChip(request.isDraft ? "DRAFT" : request.status, "Pending review")}
          </div>
          {userName && (
            <Text variant="bodySm" tone="muted">
              Submitted by {userName} · {formatSubmittedDate(request.createdAt)}
            </Text>
          )}
        </div>

        <ProductRequestImagesCard
          image={request.image || request.variants?.[0]?.image}
          galleryImages={request.galleryImages}
          productName={request.name}
        />

        <AdminDatasheetCard
          requestId={request.id}
          legacyDatasheetUrl={request.datasheetUrl}
        />

        <ProductRequestDetailsInfo request={request} />

        <ProductRequestStatusMessages
          rejectionReason={request.rejectionReason}
          odooProductId={request.odooProductTemplateId}
        />

        <ProductRequestApprovalForm
          request={request}
          onApprove={handleApprove}
          onCancel={() => navigate(backRoute)}
          isSubmitting={isSubmitting}
          readOnly={request.status !== ProductRequestDtoStatusEnum.Pending}
          title={request.status === ProductRequestDtoStatusEnum.Pending ? "Variants & Pricing" : "Variants & Pricing (Approved)"}
          subtitle="Variant specs and pricing tiers. Set the Procurtal margin per tier."
        />
      </div>

      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-borderDefault bg-bgWhite px-8 py-4 max-md:px-4 max-md:py-3">
        <Button variant="secondary" size="md" onClick={() => navigate(backRoute)}>
          Back
        </Button>
      </div>
    </div>
  );
}
